// Embed helpers — shared by the standalone /embed/* routes and by the
// Pluse / Community pages so their internal navigation stays inside the embed.

export function isEmbed() {
  try {
    return window.location.pathname.startsWith("/embed");
  } catch {
    return false;
  }
}

export function communitiesBase() {
  return isEmbed() ? "/embed/community" : "/Communities";
}

export function plusePath() {
  return isEmbed() ? "/embed/pluse" : "/pluse";
}

// Read the embed options from the current URL query string.
export function embedParams() {
  const q = new URLSearchParams(window.location.search);
  return {
    key: q.get("key") || "",
    origin: q.get("origin") || "",
    accent: q.get("accent") || "",
    accent2: q.get("accent2") || "",
    mode: q.get("mode") || "",
    radius: q.get("radius") || "",
    font: q.get("font") || "",
  };
}

// hex (#rrggbb / #rgb) -> "H S% L%" for CSS variables used by the theme.
export function hexToHslParts(hex) {
  if (!hex) return null;
  let h = String(hex).trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      default: hh = (r - g) / d + 4; break;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Apply theme options (accent color, radius, light/dark) coming from the host
// site to the embedded document by overriding the root CSS variables.
export function applyEmbedTheme({ accent, accent2, mode, radius, font } = {}) {
  const root = document.documentElement;
  const body = document.body;

  if (accent) {
    const hsl = hexToHslParts(accent);
    if (hsl) {
      root.style.setProperty("--primary", hsl);
      root.style.setProperty("--ring", hsl);
      root.style.setProperty("--accent", hsl);
    }
    root.style.setProperty("--embed-accent", accent);
  }
  if (accent2) root.style.setProperty("--embed-accent-2", accent2);
  if (radius) root.style.setProperty("--radius", `${parseFloat(radius)}rem`);

  const a1 = accent || "#9387FE";
  const a2 = accent2 || "#FF6AD5";
  // Retint the brand gradient utilities so embedded UI matches the host accent.
  let styleTag = document.getElementById("embed-theme-style");
  if (!styleTag) {
    styleTag = document.createElement("style");
    styleTag.id = "embed-theme-style";
    document.head.appendChild(styleTag);
  }
  const light = mode === "light";
  styleTag.innerHTML = `
    .gradient-text { background: linear-gradient(90deg, ${a1} 0%, ${a2} 100%) !important; -webkit-background-clip: text; background-clip: text; color: transparent; }
    [style*="linear-gradient(135deg,#9387FE"], .embed-accent-bg { background: linear-gradient(135deg, ${a1}, ${a2}) !important; }
    ${light ? `
      html, body, #root { background: #f6f6f8 !important; color: #111 !important; }
      body { background-image: none !important; }
      .glass { background: rgba(0,0,0,0.03) !important; border-color: rgba(0,0,0,0.08) !important; }
      .glass-strong { background: rgba(0,0,0,0.05) !important; border-color: rgba(0,0,0,0.10) !important; }
    ` : ``}
  `;

  if (mode === "light") body.classList.add("embed-light");
  else body.classList.remove("embed-light");

  if (font) {
    const fam = font.split(",")[0].replace(/['"]/g, "").trim();
    if (fam) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fam)}:wght@400;600;700;800&display=swap`;
      document.head.appendChild(l);
      body.style.fontFamily = `'${fam}', sans-serif`;
    }
  }
}

// Continuously report our document height to the parent window so the host
// iframe can auto-resize to fit the content (no inner scrollbar).
export function startHeightReporter() {
  const post = () => {
    try {
      const h = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight
      );
      window.parent.postMessage({ type: "lostdrkc:embed:height", height: h }, "*");
    } catch {}
  };
  post();
  const ro = new ResizeObserver(post);
  ro.observe(document.body);
  window.addEventListener("load", post);
  const t = setInterval(post, 1500);
  return () => { ro.disconnect(); clearInterval(t); };
}

// Tell the parent about auth state changes (optional hook for host sites).
export function postEmbedEvent(name, payload = {}) {
  try {
    window.parent.postMessage({ type: `lostdrkc:embed:${name}`, ...payload }, "*");
  } catch {}
}
